import type { AxiosError, AxiosResponse } from "axios";
import { isDebugAPI, logErrorToSentry } from "@litres/utils";
import type {
  IApiResponse,
  IGlobalApiParams,
  IReduxRequestNode,
  IResponse,
  IResponseErrorDetails,
} from "@litres-libs/types";
import axios from "../../instance/axios";
import { ApiType, ApiError } from "../../types/api";

/**
 * Провайдер запросов Axios (любых внешних).
 * Создает синглтон, чтобы все запросы шли через 1 точку
 */
export class AxiosPromiseProvider<TParams, TPayload> {
  private constructor() {
    // Бесполезный конструктор
  }

  private static instance: AxiosPromiseProvider<any, any>;

  /**
   * Метод, возвращающий единичный экземпляр синглтона
   * @returns Возвращает провайдер каталита для вызова запросов
   */
  public static getInstance<TParams, TPayload>(): AxiosPromiseProvider<
    TParams,
    TPayload
  > {
    if (!AxiosPromiseProvider.instance) {
      AxiosPromiseProvider.instance = new AxiosPromiseProvider<
        TParams,
        TPayload
      >();
    }

    return AxiosPromiseProvider.instance;
  }

  /**
   * Основной метод вызова запроса
   * @param params - стандартные передаваемые параметры для всех запросов в любые наши API
   * @returns {Promise} - Возвращает пропис запроса каталита
   */
  public async makeAxiosRequest(
    params: IGlobalApiParams<TParams>
  ): Promise<IReduxRequestNode<TParams, TPayload>> {
    const url = params.url || "/";
    const method = params.request ? params.request.method : "GET";
    const _params = params.request;

    const promise = await axios({
      url,
      withCredentials: true,
      method: params.request ? params.request.method : "GET",
      timeout: 40000,
      ...params.request,
    })
      .then((response: AxiosResponse<IApiResponse<TPayload>>) => {
        if (isDebugAPI) {
          console.info(
            `%cRES: ${params.provider.id}`,
            "background: #abff5f; padding: 2px 12px; color: #848484",
            response.data,
            params
          );
        }

        if (params.onSuccess) {
          params.onSuccess({
            request: params,
            response: response.data,
            error: undefined,
          });
        }

        delete params.onError;
        delete params.onSuccess;

        // В случае успешного ответа вернем ответ в fullfill
        return {
          request: params,
          response: response.data,
          error: undefined,
        };
      })
      .catch((error: AxiosError<IResponse>) => {
        if (error.response?.status === 500) {
          logErrorToSentry(new ApiError("API Error (500)"), {
            contextProperties: [
              "REQUEST_PARAMS",
              {
                url,
                method,
                params: JSON.stringify(_params),
              },
            ],
            tags: {
              api_name: ApiType.Axios,
            },
          });
        }

        if (isDebugAPI) {
          console.error(
            `%cERR: ${params.provider.id}`,
            "background: #f55e5e; padding: 2px 12px; color: #fff",
            error
          );
        }

        if (params.onError) {
          params.onError({
            request: params,
            error: {
              status: error.response?.status,
              type: (error.response?.data.error as IResponseErrorDetails).type,
              title: (error.response?.data.error as IResponseErrorDetails)
                .title,
            },
          });
        }

        delete params.onError;
        delete params.onSuccess;

        return {
          request: params,
          error: {
            status: error.response?.status,
            type: (error.response?.data.error as IResponseErrorDetails).type,
            title: (error.response?.data.error as IResponseErrorDetails).title,
          },
        } as IReduxRequestNode<TParams, TPayload>;
      });

    return promise as Promise<IReduxRequestNode<TParams, TPayload>>;
  }
}
